import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StompWebSocketService, StompConnectionState } from '../../../core/services/stomp-websocket.service';
import { getCurrentProject } from '@completion/selectors';
import { Project } from '@completion/models';
import { env } from '../../../../environments/env';

export type ActivityEventType = 'GET' | 'PUT' | 'POST' | 'DELETE' | string;

export interface ActivityNotification {
  type: ActivityEventType;
  projectId: number;
  action: string;
}

interface ActivitySample {
  event: ActivityNotification;
  receivedAt: number; // epoch ms when recorded on client
}

@Component({
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrl: './monitoring.component.scss',
  standalone: false,
})
export class MonitoringComponent implements OnDestroy {
  private readonly maxBufferSize = 100000;
  private readonly buffer: ActivitySample[] = [];

  private projectId: number | null = null;
  private wsSub: Subscription | null = null;
  private connectionSub: Subscription | null = null;
  private projectSub: Subscription | null = null;
  private metricsTickSub: Subscription | null = null;

  // Exposed metrics for template
  activitiesPerSecond = 0;
  perTypeActivitiesPerSecond: Record<ActivityEventType, number> = {};

  // Console views per type
  consoleEntries: Record<ActivityEventType, ActivitySample[]> = {
    GET: [],
    PUT: [],
    POST: [],
    DELETE: [],
  };

  // How far back in time to consider for per-second metrics (ms)
  private readonly metricsWindowMs = 5000;

  // History for charting activities per second (e.g. last 60 seconds)
  activityHistory: { name: Date; value: number }[] = [];
  readonly activityHistoryMaxPoints = 60;

  // Map an activities-per-second rate to a gauge angle between -60 and +60 degrees
  getGaugeAngle(rate: number): number {
    const maxRate = 10; // tune this based on expected peak
    const clamped = Math.max(0, Math.min(rate, maxRate));
    const fraction = clamped / maxRate; // 0..1
    return -60 + fraction * 120; // -60 (left) to +60 (right)
  }

  constructor(
    private readonly store: Store,
    private readonly stomp: StompWebSocketService,
  ) {
    this.projectSub = this.store
      .select(getCurrentProject)
      .pipe(filter((p): p is Project => !!p))
      .subscribe((project) => {
        this.projectId = project.id;
        this.ensureConnectedAndSubscribed();
      });

    // Recompute metrics on a 1s interval
    this.metricsTickSub = interval(1000).subscribe(() => {
      this.recomputeMetrics();
    });
  }

  private ensureConnectedAndSubscribed(): void {
    if (!this.projectId) {
      return;
    }

    if (!env.get().featureFlags.stomp) {
      return;
    }

    // Connect if needed
    this.stomp.connect().catch(() => {});

    // Subscribe to connection state once
    if (!this.connectionSub) {
      this.connectionSub = this.stomp.connectionState$.subscribe((state: StompConnectionState) => {
        if (state === 'CONNECTED' && this.projectId) {
          this.subscribeToActivities(this.projectId);
        }
      });
    }
  }

  private subscribeToActivities(projectId: number): void {
    // Avoid duplicate subscription for the same project
    if (this.wsSub) {
      return;
    }

    const destination = `/exchange/completion.activity/activities.${projectId}`;

    this.wsSub = this.stomp.subscribeRaw(destination, (msg) => {
      try {
        const body = JSON.parse(msg.body) as ActivityNotification;
        if (!body || body.projectId !== projectId) {
          return;
        }
        this.recordActivity(body);
      } catch {
        // ignore malformed messages
      }
    });
  }

  private recordActivity(event: ActivityNotification): void {
    const sample: ActivitySample = {
      event,
      receivedAt: Date.now(),
    };

    this.buffer.push(sample);
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.splice(0, this.buffer.length - this.maxBufferSize);
    }

    // Maintain console views per type (show latest at the top)
    const type = event.type;
    if (!this.consoleEntries[type]) {
      this.consoleEntries[type] = [];
    }
    this.consoleEntries[type].unshift(sample);
    // optional: cap console size per type to keep UI snappy
    if (this.consoleEntries[type].length > 500) {
      this.consoleEntries[type].length = 500;
    }
  }

  private recomputeMetrics(): void {
    const now = Date.now();
    const windowStart = now - this.metricsWindowMs;

    const recent = this.buffer.filter((x) => x.receivedAt >= windowStart);
    this.activitiesPerSecond = recent.length / (this.metricsWindowMs / 1000);

    const perType: Record<ActivityEventType, number> = {};
    recent.forEach((sample) => {
      const t = sample.event.type;
      perType[t] = (perType[t] || 0) + 1;
    });

    Object.keys(perType).forEach((key) => {
      perType[key as ActivityEventType] = perType[key as ActivityEventType] / (this.metricsWindowMs / 1000);
    });

    this.perTypeActivitiesPerSecond = perType;

    // Push latest overall rate into history for charting
    this.activityHistory.push({ name: new Date(now), value: this.activitiesPerSecond });
    if (this.activityHistory.length > this.activityHistoryMaxPoints) {
      this.activityHistory.splice(0, this.activityHistory.length - this.activityHistoryMaxPoints);
    }
  }

  getConsoleFor(type: ActivityEventType): ActivitySample[] {
    return this.consoleEntries[type] || [];
  }

  formatTimestamp(sample: ActivitySample): string {
    const d = new Date(sample.receivedAt);
    return d.toLocaleTimeString();
  }

  get activityChartSeries() {
    return [
      {
        name: 'Activity',
        series: this.activityHistory,
      },
    ];
  }

  ngOnDestroy(): void {
    this.projectSub?.unsubscribe();
    this.metricsTickSub?.unsubscribe();
    this.connectionSub?.unsubscribe();
    if (this.wsSub) {
      try {
        this.wsSub.unsubscribe();
      } catch {}
      this.wsSub = null;
    }
  }
}
